export interface ExportTextCell {
  kind: 'text';
  value: string;
}

export interface ExportNumberCell {
  kind: 'number';
  value: number | null;
  text: string;
}

export type ExportCell = ExportTextCell | ExportNumberCell;

export interface ExportTable {
  filename: string;
  headers: string[];
  rows: ExportCell[][];
}

export type ExportFormat = 'csv' | 'xlsx';

const CSV_MIME = 'text/csv;charset=utf-8';
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export function exportFilename(prefix: string, date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${prefix}-${year}${month}${day}`;
}

export function exportTextCell(value: string): ExportTextCell {
  return { kind: 'text', value };
}

export function exportNumberCell(
  value: number | string | null | undefined,
  text: string,
): ExportNumberCell {
  if (value === null || value === undefined || value === '') {
    return { kind: 'number', value: null, text };
  }

  const numberValue = typeof value === 'string' ? Number(value) : value;
  return { kind: 'number', value: Number.isFinite(numberValue) ? numberValue : null, text };
}

export async function downloadTableFile(table: ExportTable, format: ExportFormat): Promise<void> {
  if (format === 'csv') {
    downloadBlob(buildCsvBlob(table), `${table.filename}.csv`);
    return;
  }

  downloadBlob(await buildXlsxBlob(table), `${table.filename}.xlsx`);
}

function cellText(cell: ExportCell): string {
  return cell.kind === 'text' ? cell.value : cell.text;
}

function escapeCsv(value: string): string {
  if (/[;"\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function buildCsvBlob(table: ExportTable): Blob {
  const lines = [
    table.headers.map(escapeCsv).join(';'),
    ...table.rows.map((row) => row.map((cell) => escapeCsv(cellText(cell))).join(';')),
  ];
  return new Blob([`\uFEFF${lines.join('\r\n')}`], { type: CSV_MIME });
}

async function buildXlsxBlob(table: ExportTable): Promise<Blob> {
  const imported = await import('exceljs');
  const fallback = (imported as unknown as { default?: { Workbook?: typeof imported.Workbook } })
    .default?.Workbook;
  const workbookCtor = typeof imported.Workbook === 'function' ? imported.Workbook : fallback;
  if (!workbookCtor) {
    throw new Error('No se pudo cargar ExcelJS');
  }
  const workbook = new workbookCtor();
  const sheet = workbook.addWorksheet('Datos');
  sheet.addRow(table.headers);

  for (const row of table.rows) {
    const excelRow = sheet.addRow(row.map((cell) => cell.value));
    row.forEach((cell, index) => {
      if (cell.kind === 'number' && cell.value != null) {
        excelRow.getCell(index + 1).numFmt = '#,##0.00';
      }
    });
  }

  sheet.getRow(1).font = { bold: true };
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], { type: XLSX_MIME });
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
